import type { FacetFormModel, FacetOp, TransformEntry } from "./facet-form-config";
import {
    isEmptyTransformEditor,
    parseJsonToFormModel,
    transformEntriesToObject,
} from "./facet-form-helpers";

export type TransformAxis = "row" | "col";

export function hasTransformMappings(entries: TransformEntry[] | undefined): boolean {
    return (entries ?? []).some((e) => e.key !== "" || e.value.trim() !== "");
}

/** Draft ready to apply: original selected; mapped value may be pre-filled from last apply. */
export function hasDraftTransformSelection(entries: TransformEntry[] | undefined): boolean {
    return (entries ?? []).some((e) => e.key !== "" && e.value.trim() !== "");
}

function committedTransformKeys(committed: TransformEntry[] | undefined): string[] {
    return Object.keys(transformEntriesToObject(committed ?? [])).filter((k) => k !== "");
}

/** Entries in editor that differ from committed ETL transform key-value pairs. */
function hasDraftBeyondCommitted(
    draft: TransformEntry[] | undefined,
    committed: TransformEntry[] | undefined
): boolean {
    if (isEmptyTransformEditor(draft)) {
        return committedTransformKeys(committed).length > 0;
    }
    const committedObj = transformEntriesToObject(committed ?? []);
    return (draft ?? []).some((e) => {
        if (e.key === "" || !e.value.trim()) return false;
        const committedValue = committedObj[e.key];
        return committedValue === undefined || committedValue !== e.value;
    });
}

export function hasUncommittedTransformDraft(
    model: FacetFormModel,
    committedJson: string
): boolean {
    const committed = parseJsonToFormModel(committedJson);
    if (!committed) {
        return model.ops.some(
            (op) =>
                op.type === "pivot" &&
                (hasDraftTransformSelection(op.row_transform) ||
                    hasDraftTransformSelection(op.col_transform))
        );
    }

    return model.ops.some((op, i) => {
        if (op.type !== "pivot") return false;
        const committedOp = committed.ops[i]?.type === "pivot" ? committed.ops[i] : null;
        return (
            hasDraftBeyondCommitted(op.row_transform, committedOp?.row_transform) ||
            hasDraftBeyondCommitted(op.col_transform, committedOp?.col_transform)
        );
    });
}

export function normalizeFacetJsonForCompare(json: string): string {
    try {
        return JSON.stringify(JSON.parse(json || "{}"));
    } catch {
        return json.trim();
    }
}

export function validateTransformApply(
    op: FacetOp | undefined,
    axis: TransformAxis,
    entries: TransformEntry[] | undefined
): string | null {
    if (!op || op.type !== "pivot") {
        return "Apply transform requires a pivot operation";
    }
    const axisKey = (axis === "row" ? op.row_key : op.col_key)?.trim() ?? "";
    if (!axisKey) {
        return axis === "row" ? "Row key is required" : "Col key is required";
    }
    if (isEmptyTransformEditor(entries)) {
        return null;
    }
    if (!(entries ?? []).some((e) => e.key !== "")) {
        return "Select an original value to map";
    }
    if (!(entries ?? []).some((e) => e.value.trim() !== "")) {
        return "Mapped value is required";
    }
    return null;
}
